import React, { useEffect, useState } from 'react';
import Typography from '../../../../components/atoms/typography';
import { Box, Grid, Modal } from '@material-ui/core';
import InputDisabled from '../../../../components/atoms/inputDisabled';
import ButtonComponent from '../../../../components/atoms/button';
import { Controller, useFormContext } from 'react-hook-form';
import { useRegisterFreights } from '../../../../hooks/freights/useRegisterFreights';
import ReorderRoute from '../../../../components/reorderList';
import AlertWarning from '../../../../components/atoms/alert';
import {
  freightTypeEnum,
  useConfigureFreight,
} from '../../../../hooks/freights/useConfigureFreight';
import moment from 'moment';
import { Row } from '../../../../hooks/carts/useNearGetCarts';
import useGetDeliveriesRequests from '../../../../hooks/delivery-requests';
import jwtDecode from 'jwt-decode';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { FreightDetails } from '../configureFreight';
import { DeliveryRequest } from '../../../../store/ducks/freight/types';
import ModalCustomizeFreight from '../../../organisms/modal';
import { useUpdateCustomPriceFreight } from '../../../../hooks/freights/useUpdateCustomPriceFreight';
import { useUpdateFreight } from '../../../../hooks/freights/useUpdateFreight';
import ModalCustom from '../../../atoms/modalCustom';
import { defaultFormatKilometrageNumbers } from '../../../../utils/formatKilometrageNumber';

interface checkImportProps {
  onNextStep: () => void;
  onPrevStep: (customStep?: number) => void;
  publishFreight?: boolean;
  setPublishFreight?: React.Dispatch<React.SetStateAction<boolean>>;
  loading?: boolean;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  mode?: 'edit' | 'create';
  freightId?: string;
}

const CheckImport: React.FC<checkImportProps> = ({
  onNextStep,
  onPrevStep,
  setPublishFreight,
  setLoading: setParentLoading,
  mode,
  freightId: propFreightId,
}) => {
  const { control, getValues, setValue, reset } =
    useFormContext<FreightDetails>();
  const freightDetails = getValues();
  const token = useSelector(
    (state: ApplicationState) => state.distributor.token,
  );
  const tokenDecoded: any = jwtDecode(token);

  const [modalOpen, setModalOpen] = useState(false);
  const { execute: registerFreight, loading: registerFreightLoading } = useRegisterFreights();
  const { execute: configureFreight, loading: configureFreightLoading } = useConfigureFreight();
  const { execute: getDeliveryRequest } = useGetDeliveriesRequests();

  const [listReordered, setListReordered] = useState(false);
  const [deliveriesToReorder, setDeliveriesToReorder] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [modalPublishOpen, setModalPublishOpen] = useState(false);

  const { execute, loading: customPriceLoading } = useUpdateCustomPriceFreight();
  const { execute: updateFreight, loading: updateFreightLoading } = useUpdateFreight();
  const [selectedValue, setSelectedValue] = useState<string>('option1');
  const isoDate = freightDetails.createdAt;
  const date = moment(isoDate);

  const handleReorder = (newOrder: any[]) => {
    setListReordered(true);
    setDeliveriesToReorder(newOrder);
  };

  // Calcular percentual baseado no freightCost e totalValue, mas usar percentageFreightCost do freightRequest se disponível
  const calculatePercent = (): number | null => {
    // Se temos percentageFreightCost do freightRequest, usar ele (convertido de centésimos para decimal)
    if (freightDetails.percentageFreightCost !== null && freightDetails.percentageFreightCost !== undefined) {
      return Number(freightDetails.percentageFreightCost) / 100;
    }
    
    // Caso contrário, calcular baseado em freightCost e totalValue
    // Fórmula correta: (freightCost / totalValue) * 100
    if (Number(freightDetails.freightCost) > 0 && Number(freightDetails.totalValue) > 0) {
      const percentage = (Number(freightDetails.freightCost) / Number(freightDetails.totalValue)) * 100;
      return Math.round(percentage * 100) / 100;
    }
    
    // Se não conseguir calcular, retornar null (backend vai preservar valor existente)
    return null;
  };

  const percentValue = calculatePercent();
  const percent = percentValue !== null 
    ? percentValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '';

  const handleRecalculed = async () => {
    setModalOpen(true);
  };

  const handleOpenModal = () => {
    setModalPublishOpen(true);
  };

  const handlePublicFreightAction = () => {
    if (freightDetails.freightType === freightTypeEnum.INTERNAL) {
      handlePublishClick();
    } else {
      listReordered ? handleRecalculed() : handleOpenModal();
    }
  };

  const handleOpenModalBack = () => {
    setModalOpen(true);
  };

  const handleRecalculedFreight = async () => {
    try {
      setIsRecalculating(true);
      const waypointsNames = deliveriesToReorder.some(el => el.locked)
      ? deliveriesToReorder.map((item, index, array) => {
          if (item.locked) {
            const destinations = array
              .slice(index + 1)
              .map((_, subIndex) => `destination${index + 2 + subIndex}`)
              .join(',');
            return {
              corporateName: item.corporateName,
              before: destinations,
            };
          } else {
            return { corporateName: item.corporateName };
          }
        })
      : deliveriesToReorder.map((item, index, array) => {
          return { corporateName: item.corporateName };
        });

    // Usar percentageFreightCost do freightRequest se disponível, senão usar o calculado
    // Se percent estiver vazio (valores zerados), não enviar (backend vai preservar valor existente)
    let percentageToSend: string | undefined = undefined;
    
    // Debug: verificar valores calculados
    console.log('[CheckImport] Debug percentual:', {
      percentValue,
      percent,
      freightCost: freightDetails.freightCost,
      totalValue: freightDetails.totalValue,
      percentageFreightCostFromDB: freightDetails.percentageFreightCost,
    });
    
    if (percent && percent !== '0,00' && percent !== '0.00' && percent !== '' && parseFloat(percent.replace(',', '.')) > 0) {
      // Usar o percentual calculado se for válido
      percentageToSend = percent;
      console.log('[CheckImport] Usando percentual calculado:', percentageToSend);
    } else if (freightDetails.percentageFreightCost !== null && freightDetails.percentageFreightCost !== undefined && freightDetails.percentageFreightCost > 0) {
      // Se o calculado for inválido, usar o do freightRequest
      percentageToSend = (Number(freightDetails.percentageFreightCost) / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      console.log('[CheckImport] Usando percentual do banco:', percentageToSend);
    }
    // Se ambos forem inválidos, deixar undefined para o backend preservar o valor existente
    
    console.log('[CheckImport] Percentual que será enviado:', percentageToSend);

    const data = await configureFreight({
      freightRequestId: freightDetails.freightRequestId,
      totalWeight: freightDetails.totalWeight,
      totalDeliveries: freightDetails.totalDeliveries,
      totalValue: freightDetails.totalValue,
      numberOfHelpers: freightDetails.numberOfHelpers,
      waypoints: waypointsNames,
      pickUpDate: freightDetails.pickUpDate,
      pickUpTime: freightDetails.pickUpTime,
      freightType: freightDetails.freightType,
      vehicleTypeId: freightDetails.vehicleTypeId,
      extraCartIds: freightDetails.extraCartIds,
      percentageFreightCost: percentageToSend,
    });

    // Debug: verificar o que foi retornado
    console.log('[CheckImport] Resposta completa do configureFreight:', {
      data,
      freightCost: data?.freightCost,
      totalKilometers: data?.totalKilometers,
      priceWithFee: data?.priceWithFee,
      freightCostAtual: freightDetails.freightCost,
    });

    // Atualizar freightCost apenas se o valor retornado for válido
    if (data?.freightCost !== undefined && data?.freightCost !== null && !isNaN(Number(data.freightCost)) && Number(data.freightCost) > 0) {
      setValue('freightCost', Number(data.freightCost));
      console.log('[CheckImport] ✅ freightCost atualizado:', Number(data.freightCost));
    } else if (data?.priceWithFee !== undefined && data?.priceWithFee !== null && !isNaN(Number(data.priceWithFee)) && Number(data.priceWithFee) > 0) {
      // Fallback: usar priceWithFee se freightCost não estiver disponível
      setValue('freightCost', Number(data.priceWithFee));
      console.log('[CheckImport] ✅ freightCost atualizado via priceWithFee:', Number(data.priceWithFee));
    } else {
      console.warn('[CheckImport] ⚠️ freightCost inválido ou zerado do backend:', {
        freightCost: data?.freightCost,
        priceWithFee: data?.priceWithFee,
        dataCompleta: data,
      });
      // Se o valor for inválido, manter o valor atual do formulário (não zerar)
      if (freightDetails.freightCost && freightDetails.freightCost > 0) {
        console.log('[CheckImport] Mantendo valor atual do formulário:', freightDetails.freightCost);
      } else {
        console.error('[CheckImport] ❌ ERRO: Valor atual também está zerado!', freightDetails.freightCost);
      }
    }
    
    if (data?.totalKilometers !== undefined && data?.totalKilometers !== null && !isNaN(Number(data.totalKilometers))) {
      setValue('totalKilometers', Number(data.totalKilometers));
    }

    if (setPublishFreight) {
      setPublishFreight(true);
    }
    setModalOpen(false);
    setListReordered(false);
    setIsRecalculating(false);
    } catch (error) {
      setIsRecalculating(false);
    }
  };

  const handlePrev = () => {
    reset({
      loadNumber: '',
      createdAt: new Date(),
      freightCost: 0,
      totalValue: 0,
      totalKilometers: 0,
      waypoints: [],
      deliveryRequests: [],
    });
    onPrevStep(2);
  };

  const handlePublishClick = async () => {
    // Fechar o modal imediatamente
    setModalPublishOpen(false);
    
    try {
      setIsLoading(true);
      if (setParentLoading) setParentLoading(true);
      const updatedFreightCost = getValues('freightCost');
      const allValues = getValues() as any;
      const freightId = String(propFreightId || '');

      if (mode === 'edit' && freightId && freightId !== '') {
        const coercedFreightType =
          freightDetails.freightType === freightTypeEnum.PRIVATE &&
          (freightDetails as any).vehicleId
            ? freightTypeEnum.INTERNAL
            : freightDetails.freightType;

        const updatePayload: any = {
          pickUpDate: freightDetails.pickUpDate,
          deliveryLimitDate: freightDetails.deliveryLimitDate,
          numberOfHelpers: String(freightDetails.numberOfHelpers),
          freightStatus: 'PUBLISHED',
          freightType: coercedFreightType,
        };

        if (
          freightDetails.pickUpTime &&
          freightDetails.pickUpTime.trim() !== ''
        ) {
          updatePayload.pickUpTime = freightDetails.pickUpTime;
        }

        if ((freightDetails as any).driverId) {
          updatePayload.driverId = (freightDetails as any).driverId;
        }
        if ((freightDetails as any).vehicleId) {
          updatePayload.vehicleId = (freightDetails as any).vehicleId;
        }

        // Se houver valor customizado, salvar ANTES de publicar e aguardar confirmação
        if (selectedValue === 'option2' && updatedFreightCost) {
          console.log('[CheckImport] Salvando valor customizado ANTES de publicar:', updatedFreightCost);
          // Salvar o valor customizado primeiro
          await execute(freightId, {
            freightCost: `${updatedFreightCost}`,
          });
          console.log('[CheckImport] ✅ Valor customizado salvo com sucesso');
          
          // Aguardar mais tempo para garantir que o valor foi persistido no banco e commitado
          console.log('[CheckImport] Aguardando persistência no banco...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          console.log('[CheckImport] Prosseguindo com publicação...');
        }

        await updateFreight(freightId, updatePayload);
        console.log('[CheckImport] ✅ Frete publicado');

        onNextStep();
        setTimeout(() => {
          if (setPublishFreight) {
            setPublishFreight(true);
          }
          setIsLoading(false);
          if (setParentLoading) setParentLoading(false);
        }, 3000);
      } else {
        const response = await registerFreight({
          distributorId: tokenDecoded.distributorId,
          freightRequestId: freightDetails.freightRequestId,
        });

        if (response?.status === 201 && selectedValue === 'option2') {
          await execute(response?.data.freightId, {
            freightCost: `${updatedFreightCost}`,
          });
          onNextStep();
        } else {
          onNextStep();
        }

        setTimeout(() => {
          if (setPublishFreight) {
            setPublishFreight(true);
          }
          setIsLoading(false);
          if (setParentLoading) setParentLoading(false);
        }, 3000);
      }
    } catch (error: any) {
      console.error('Error: ', error);
      setIsLoading(false);
      if (setParentLoading) setParentLoading(false);
    }
  };

  const handleChangeSelectedValue = (value: string) => {
    setSelectedValue(value);
  };

  useEffect(() => {
    const fetchDeliveryRequestData = async () => {
      try {
        const data = await getDeliveryRequest(freightDetails.freightRequestId);
        reset({
          ...freightDetails,
          loadNumber: data?.deliveryRequests[0]?.loadNumber,
          createdAt: data?.createdAt,
          freightCost: data?.freightCost,
          totalValue: data?.totalValue,
          totalKilometers: data?.totalKilometers,
          waypoints: data?.waypoints,
          deliveryRequests: data?.deliveryRequests,
          driver: data?.driver,
          vehicle: data?.vehicle,
          percentageFreightCost: data?.percentageFreightCost, // Preservar percentageFreightCost do backend
        });
      } catch (error) {
        console.error('Erro ao buscar dados da requisição de entrega:', error);
      }
    };

    if (freightDetails.freightRequestId) {
      fetchDeliveryRequestData();
    }
  }, [freightDetails.freightRequestId]);

  useEffect(() => {
    const unifiedItems = (freightDetails.deliveryRequests || []).reduce(
      (acc: { [key: string]: Row }, item: DeliveryRequest) => {
        if (acc[item.corporateName]) {
          acc[item.corporateName].totalWeight += item.totalWeight;
        } else {
          acc[item.corporateName] = {
            corporateName: item.corporateName,
            distance: 0,
            address: item.address,
            cartId: item.deliveryRequestId,
            totalWeight: item.totalWeight,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
          };
        }
        return acc;
      },
      {},
    );

    if (freightDetails.extraCarts && freightDetails.extraCarts.length > 0) {
      freightDetails.extraCarts.forEach((item: Row) => {
        if (unifiedItems[item.corporateName]) {
          unifiedItems[item.corporateName].totalWeight += item.totalWeight;
        } else {
          unifiedItems[item.corporateName] = {
            corporateName: item.corporateName,
            distance: 0,
            address: item.address,
            cartId: item.cartId,
            totalWeight: item.totalWeight,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
          };
        }
      });
    }

    const sortedList = (freightDetails.waypoints || [])
      .map(el => unifiedItems[el])
      .filter(el => el !== undefined);
    setDeliveriesToReorder(sortedList);
  }, [freightDetails.waypoints]);

  const FreightCalculationDataTable: React.FC = () => {
    const gridItemSpace =
      freightDetails.freightType === freightTypeEnum.INTERNAL ? true : 4;

    return (
      <Grid container spacing={2} style={{ marginTop: 2 }}>
        <Grid item xs={gridItemSpace}>
          <Controller
            name="totalDeliveries"
            control={control}
            render={({ field: { value } }) => (
              <InputDisabled
                label={'Total de entregas'}
                defaultValue={freightDetails.totalDeliveries}
              />
            )}
          />
        </Grid>
        <Grid item xs={gridItemSpace}>
          <Controller
            name="totalWeight"
            control={control}
            render={({ field: { value, onChange } }) => (
              <InputDisabled
                label={'Peso total'}
                value={value.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                onChange={onChange}
                endAdornment={'kg'}
              />
            )}
          />
        </Grid>
        <Grid item xs={gridItemSpace}>
          <Controller
            name="totalKilometers"
            control={control}
            render={({ field: { value } }) => (
              <InputDisabled
                label={'Quilometragem estimada'}
                value={defaultFormatKilometrageNumbers(
                  freightDetails.totalKilometers,
                )}
                endAdornment={'km'}
              />
            )}
          />
        </Grid>

        {freightDetails.freightType !== freightTypeEnum.INTERNAL && (
          <Grid item xs={gridItemSpace}>
            <InputDisabled
              size="small"
              label={'Percentual do frete'}
              defaultValue={percent}
              value={percent}
              endAdornment={'%'}
            />
          </Grid>
        )}

        {freightDetails.freightType !== freightTypeEnum.INTERNAL && (
          <Grid item xs={gridItemSpace}>
            <Controller
              name="freightCost"
              control={control}
              render={({ field: { value } }) => (
                <InputDisabled
                  label={'Custo do frete'}
                  value={Number(freightDetails.freightCost).toLocaleString(
                    'pt-BR',
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                  )}
                  startAdornment={'R$'}
                />
              )}
            />
          </Grid>
        )}

        <Grid item xs={gridItemSpace}>
          <InputDisabled
            label={'Valor total das mercadorias'}
            value={
              freightDetails.totalValue !== null
                ? Number(freightDetails.totalValue).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : ''
            }
            startAdornment={'R$'}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <ModalCustom
        open={modalOpen}
        label={'Você tem certeza que deseja voltar?'}
        children={
          'Ao retornar, todos os dados não salvos serão perdidos e será necessário importar um novo frete.'
        }
        onClickPublish={handlePrev}
        onCancelClick={() => setModalOpen(false)}
        ButtonOutlinePrimary="Não"
        ButtonPrimary="Sim"
      />
      {listReordered ? (
        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AlertWarning
            isOpen={modalOpen}
            onCancelClick={() => setModalOpen(false)}
            onConfirmClick={handleRecalculedFreight}
            loading={isRecalculating}
          />
        </Modal>
      ) : (
        <ModalCustomizeFreight
          open={modalPublishOpen}
          onCancelClick={() => setModalPublishOpen(false)}
          onNextStep={handlePublishClick}
          onChangeSelectedValue={handleChangeSelectedValue}
          selectedValue={selectedValue}
        />
      )}
      <Box component={'div'}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography mb={2} fontWeight="700" color="secondary">
              Número da carga
            </Typography>
            <Controller
              name="loadNumber"
              control={control}
              render={({ field: { value } }) => (
                <Typography fontWeight="400" color="secondary">
                  {freightDetails.loadNumber}
                </Typography>
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography mb={2} fontWeight="700" color="secondary">
              Criado em
            </Typography>
            <Controller
              name="createdAt"
              control={control}
              render={({ field: { value } }) => (
                <Typography fontWeight="400" color="secondary">
                  {date.format('DD/MM/YYYY')}
                </Typography>
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography mb={2} fontWeight="700" color="secondary">
              Tipo de veículo
            </Typography>
            <Controller
              name="vehicleType"
              control={control}
              render={({ field: { value } }) => (
                <Typography fontWeight="400" color="secondary">
                  {value || '--'}
                </Typography>
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography mb={2} fontWeight="700" color="secondary">
              Data da retirada
            </Typography>
            <Controller
              name="pickUpDate"
              control={control}
              render={({ field: { value } }) => (
                <Typography fontWeight="400" color="secondary">
                  {moment(freightDetails.pickUpDate).format('DD/MM/YYYY')}
                </Typography>
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography mb={2} fontWeight="700" color="secondary">
              Horário da retirada
            </Typography>
            <Controller
              name="pickUpTime"
              control={control}
              render={({ field: { value } }) => (
                <Typography fontWeight="400" color="secondary">
                  {freightDetails.pickUpTime}
                </Typography>
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography mb={2} fontWeight="700" color="secondary">
              Data limite de entrega
            </Typography>
            <Controller
              name="deliveryLimitDate"
              control={control}
              render={({ field: { value } }) => (
                <Typography fontWeight="400" color="secondary">
                  {moment(freightDetails.deliveryLimitDate).format(
                    'DD/MM/YYYY',
                  )}
                </Typography>
              )}
            />
          </Grid>

          {freightDetails.freightType !== freightTypeEnum.PUBLIC && (
            <>
              <Grid item xs={4}>
                <Typography mb={2} fontWeight="700" color="secondary">
                  Veículo
                </Typography>
                <Typography fontWeight="400" color="secondary">
                  {freightDetails.vehicle?.model || '--'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography mb={2} fontWeight="700" color="secondary">
                  Placa do veículo
                </Typography>
                <Typography fontWeight="400" color="secondary">
                  {freightDetails.vehicle?.plate || '--'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography mb={2} fontWeight="700" color="secondary">
                  Motorista
                </Typography>
                <Typography fontWeight="400" color="secondary">
                  {freightDetails.driver?.username}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography mb={2} fontWeight="700" color="secondary">
                  Contato - Telefone
                </Typography>
                <Typography fontWeight="400" color="secondary">
                  {freightDetails.driver?.phoneNumber}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography mb={2} fontWeight="700" color="secondary">
                  Contato - E-mail
                </Typography>
                <Typography fontWeight="400" color="secondary">
                  {freightDetails.driver?.email}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
        <hr />
        <Typography fontWeight="700" color="secondary">
          Cálculo do frete
        </Typography>

        <FreightCalculationDataTable />

        <hr />

        <ReorderRoute items={deliveriesToReorder} onReorder={handleReorder} />

        <Grid container justifyContent="space-between" style={{ marginTop: 5 }}>
          <ButtonComponent
            theme="outline-primary"
            justifyContent="flex-start"
            onClick={handleOpenModalBack}
          >
            Voltar
          </ButtonComponent>
          <ButtonComponent
            theme="primary"
            justifyContent="flex-end"
            onClick={handlePublicFreightAction}
            disabled={
              listReordered
                ? isRecalculating || configureFreightLoading
                : registerFreightLoading || updateFreightLoading || customPriceLoading
            }
            loading={
              listReordered
                ? isRecalculating || configureFreightLoading
                : registerFreightLoading || updateFreightLoading || customPriceLoading
            }
          >
            {listReordered ? 'Recalcular Frete' : 'Publicar frete'}
          </ButtonComponent>
        </Grid>
      </Box>
    </>
  );
};

export default CheckImport;
